import { useState } from "react";
import {
  ORG_ADMIN_EDITABLE_MANAGED_SETTINGS_KEYS,
  BUILTIN_AGENT_ROLE_DEFAULTS,
  ADMIN_ROLE_TEMPLATE_ID,
  MANAGED_PERMISSION_MODES,
  buildManagedPermissions,
  AGENT_CONNECTOR_MODES,
  buildConnectorTiers,
  normalizeDelegateCodexConfig,
  pruneBrainGroupDefaultsToCatalog,
  type BrainGroupDefaultsRemoval,
  type BrainGroupParams,
  type CatalogModelIdRow,
} from "@dosia/shared";
import {
  FormField,
  TextArea,
  TextInput,
} from "../../../components/FormField.js";

/**
 * T1.4.3 · RoleWorkspaceConfig 结构化编辑器（dosia 策略 tab）。
 *
 * 暴露 dosia 原生字段（不引入 menu/tool/workflow/field 新字段，CLAUDE.md 红线 + 用户决议
 * §plan-admin-portal-post-e2e 选项 A）：
 *   - templateId / effort 基础项（角色不配默认模型——N-94，员工默认模型只由平台默认模型
 *     优先级决定；旧 dosia.model 保存时不再带回，服务端写入口另有同一剥离兜底）
 *   - disallowedTools / pluginTags / managedPlugins（白名单·强制启用）/ forbiddenPlugins
 *     （黑名单·永不加载）/ forbiddenSkills / managedSkills（"all" 走特殊语义）：
 *     tag 输入（逗号分隔字符串编辑，提交时切回 string[]）
 *   - skillPolicy / managedSettings / sandbox / hooks 嵌套结构深：JSON 子 textarea
 *     fallback（用户决议 N — 这次不做完整 form UI）。
 *
 * 不动 server schema；提交后整个 dosia 对象覆盖 PATCH。
 */

export interface PolicyEditorValue {
  templateId: string;
  effort: string;
  disallowedTools: string;
  pluginTags: string;
  forbiddenPlugins: string;
  forbiddenMcpServers: string;
  managedPlugins: string;
  forbiddenSkills: string;
  managedSkills: string;
  /** "all" / 留空 / 多个 skill 名（与 forbidden 重合时后者优先）*/
  skillPolicyJson: string;
  /** 预算与模型策略（managedSettings 下放子字段，2026-07-03）——结构化编辑，
   *  hydrate 时从 managedSettings 抽出，serialize 时合回；managedSettingsJson
   *  只承载剩余（安全类）子字段。 */
  maxAllowedBudgetUsd: string;
  /** 角色级每员工每日云任务告警额度 USD（2026-07-10）：正数与 org 级收严取小；
   *  留空=继承 org 级，仅 org 级也未设置时才无限
   *  （合并在 dosia-enterprise buildEmployeeManagedSettings，非本层）。 */
  cloudTaskDailyBudgetUsd: string;
  maxAllowedEffort: string;
  visibleGroups: string;
  lockRoleMapping: boolean;
  /** 本角色是否启用 SDK 记忆（P2）。缺省开 —— 勾选=开（不下发，缺省语义），
   *  取消=关（serialize 出 managedSettings.orgMemoryEnabled=false，收严合并）。 */
  orgMemoryEnabled: boolean;
  /** 是否允许员工自己开/关「记忆读取」（P5-A）。缺省给 —— 勾选=给（不下发，缺省语义），
   *  取消=收走（serialize 出 managedSettings.memoryReadToggleAllowed=false）——员工端
   *  记忆面板只剩统计+只读浏览，读取恒按 orgMemoryEnabled 决定，见 MemoryTab。 */
  memoryReadToggleAllowed: boolean;
  /** G-K5 员工日/周额度（USD，顶层 dosia.memberQuota，非 managedSettings 子字段）。
   *  留空 = 无限。透传给网关写 api_key RateLimit1d/7d，调用时强制。 */
  memberQuotaDaily: string;
  memberQuotaWeekly: string;
  /** 内置 Agent 可见性覆盖（2026-07-03 下放）。Key=agent id，值=可见 templateId 列表。
   *  UI 只按「本角色可见与否」勾选，编码规则见 AgentVisibilityField。 */
  agentRoleBindings: Record<string, string[]>;
  /** OPT-2 权限层（managedSettings.permissions 下放子字段，2026-07-11）——结构化编辑。
   *  三数组以 JSON 文本为单一真相源（高级区直编 + 工具三态/白话开关派生视图双向同步）；
   *  serialize 前过 buildManagedPermissions 校验。hydrate 从 managedSettings.permissions
   *  抽出，serialize 合回。 */
  permissionsAllow: string;
  permissionsAsk: string;
  permissionsDeny: string;
  permissionsDefaultMode: string;
  permissionsDisableBypass: boolean;
  /** permissions 里本卡片没建模的子字段（additionalDirectories / 未来 SDK 新键）passthrough，
   *  不丢；仅在非空时暴露裸 JSON 编辑。 */
  permissionsExtraJson: string;
  /** OPT-6 开放连接自助接入（managedSettings.connectors / agentConnectorMode 下放子字段，
   *  2026-07-11）。三档布尔（勾=开，默认全开；取消=关该档，serialize 出 connectors[x]=false）
   *  + agent 自助接入模式下拉。唯一构建器 buildConnectorTiers 把关形态。 */
  connectorRemote: boolean;
  connectorNpm: boolean;
  connectorCommand: boolean;
  agentConnectorMode: string;
  /** F4 · Codex 委派开关（顶层 dosia.delegateCodex，唯一归一化 normalizeDelegateCodexConfig，
   *  见 shared types/index.ts DelegateCodexConfig）。缺省关——勾选=开。 */
  delegateCodexEnabled: boolean;
  /** 委派执行通道："personal"（缺省，走员工本机 ~/.codex 个人登录态）/ "enterprise"
   *  （免登录，走企业网关）/ "personal-first"（有个人态用个人、否则回落企业）。 */
  delegateCodexChannelMode: string;
  /** 委派沙箱权限地板："workspace-write"（缺省，允许改盘）/ "read-only"（强制只读，即便
   *  主脑传 readOnly:false 也收窄）。 */
  delegateCodexMaxSandbox: string;
  /** 委派改动过文件后，本轮停笔前强制主脑先复核（Stop hook 评审门）。缺省关。 */
  delegateCodexReviewGate: boolean;
  /** 企业通道默认委派模型 id。留空 = 用可用清单首个。 */
  delegateCodexDefaultModel: string;
  managedSettingsJson: string;
  sandboxJson: string;
  hooksJson: string;
  /** Role-provisioned MCP servers — Record<name, McpServerEntry>. 分发给该角色全员
   *  （登录合进 workspace.mcpServers 标 managed 只读）。本期 JSON 编辑。 */
  mcpServersJson: string;
}

export const EMPTY_POLICY_VALUE: PolicyEditorValue = {
  templateId: "",
  effort: "",
  disallowedTools: "",
  pluginTags: "",
  forbiddenPlugins: "",
  forbiddenMcpServers: "",
  managedPlugins: "",
  forbiddenSkills: "",
  managedSkills: "",
  skillPolicyJson: "",
  maxAllowedBudgetUsd: "",
  cloudTaskDailyBudgetUsd: "",
  maxAllowedEffort: "",
  visibleGroups: "",
  lockRoleMapping: false,
  orgMemoryEnabled: true,
  memoryReadToggleAllowed: true,
  memberQuotaDaily: "",
  memberQuotaWeekly: "",
  agentRoleBindings: {},
  permissionsAllow: "",
  permissionsAsk: "",
  permissionsDeny: "",
  permissionsDefaultMode: "",
  permissionsDisableBypass: false,
  permissionsExtraJson: "",
  connectorRemote: true,
  connectorNpm: true,
  connectorCommand: true,
  agentConnectorMode: "auto",
  delegateCodexEnabled: false,
  delegateCodexChannelMode: "personal",
  delegateCodexMaxSandbox: "workspace-write",
  delegateCodexReviewGate: false,
  delegateCodexDefaultModel: "",
  managedSettingsJson: "",
  sandboxJson: "",
  hooksJson: "",
  mcpServersJson: "",
};

/** 从现有 dosia 对象 hydrate 编辑器表单值。未知字段 → 空字符串。 */
export function hydratePolicyValue(dosia: unknown): PolicyEditorValue {
  const d = (dosia ?? {}) as Record<string, unknown>;
  return {
    templateId: typeof d.templateId === "string" ? d.templateId : "",
    effort: typeof d.effort === "string" ? d.effort : "",
    disallowedTools: arrToCsv(d.disallowedTools),
    pluginTags: arrToCsv(d.pluginTags),
    forbiddenPlugins: arrToCsv(d.forbiddenPlugins),
    forbiddenMcpServers: arrToCsv(d.forbiddenMcpServers),
    managedPlugins: arrToCsv(d.managedPlugins),
    forbiddenSkills: arrToCsv(d.forbiddenSkills),
    managedSkills:
      d.managedSkills === "all"
        ? "all"
        : arrToCsv(d.managedSkills),
    skillPolicyJson:
      d.skillPolicy === undefined ? "" : JSON.stringify(d.skillPolicy, null, 2),
    ...hydrateManagedSettings(d.managedSettings),
    ...hydrateMemberQuota(d.memberQuota),
    agentRoleBindings: hydrateBindings(d.agentRoleBindings),
    // dosia.modelSelection（角色可见性 + 每模型参数）不由本编辑器建模——它是统一「模型治理」
    // 面 scope=角色 的唯一编辑者。serialize 时作为 base 未建模字段整体 opaque 透传（见
    // MANAGED_DOSIA_KEYS 已不含 "modelSelection"），故此处无需 hydrate 出任何表单值。
    ...hydrateDelegateCodex(d.delegateCodex),
    sandboxJson:
      d.sandbox === undefined ? "" : JSON.stringify(d.sandbox, null, 2),
    hooksJson: d.hooks === undefined ? "" : JSON.stringify(d.hooks, null, 2),
    mcpServersJson:
      d.mcpServers === undefined ? "" : JSON.stringify(d.mcpServers, null, 2),
  };
}

/** 表单建模的 dosia key 集合 — serialize 时这些以表单为准，其余从原对象透传。 */
const MANAGED_DOSIA_KEYS = new Set([
  "templateId",
  // "model" 已退役（N-94）：不建模也不透传——留在本集合里，base 透传阶段即跳过它，
  // 保存时旧 role.dosia.model 被丢弃。
  "model",
  "effort",
  "disallowedTools",
  "pluginTags",
  "forbiddenPlugins",
  "forbiddenMcpServers",
  "managedPlugins",
  "forbiddenSkills",
  "managedSkills",
  "skillPolicy",
  "managedSettings",
  "sandbox",
  "hooks",
  "mcpServers",
  "agentRoleBindings",
  "memberQuota",
  // 注意：**不含** "modelSelection" —— 角色的模型可见性 + 每模型参数只在统一「模型治理」
  // 面（scope=角色）编辑；本 policy tab 保存时作为未建模字段整体 opaque 透传，绝不重建/抹改
  // （红线 #9：单一编辑者，消除双写。旧 RoleModelCurationField 已于 P2c 移除）。
  "delegateCodex",
]);

/**
 * 把表单值序列化回 dosia 对象（提交时调用）。返回 ok 分支或 error。
 *
 * `base` = 编辑前的 role.dosia：表单没建模的字段（name / templateVersion /
 * icon …）原样透传，否则「policy tab 保存」会静默抹掉它们。
 */
export function serializePolicyValue(
  value: PolicyEditorValue,
  base?: unknown,
): { ok: true; dosia: Record<string, unknown> } | { ok: false; error: string } {
  const out: Record<string, unknown> = {};
  if (base && typeof base === "object" && !Array.isArray(base)) {
    for (const [k, v] of Object.entries(base as Record<string, unknown>)) {
      if (!MANAGED_DOSIA_KEYS.has(k)) out[k] = v;
    }
  }
  if (value.templateId.trim()) out.templateId = value.templateId.trim();
  if (value.effort.trim()) out.effort = value.effort.trim();
  const disallowedTools = csvToArr(value.disallowedTools);
  if (disallowedTools.length > 0) out.disallowedTools = disallowedTools;
  const pluginTags = csvToArr(value.pluginTags);
  if (pluginTags.length > 0) out.pluginTags = pluginTags;
  const forbiddenPlugins = csvToArr(value.forbiddenPlugins);
  if (forbiddenPlugins.length > 0) out.forbiddenPlugins = forbiddenPlugins;
  const forbiddenMcpServers = csvToArr(value.forbiddenMcpServers);
  if (forbiddenMcpServers.length > 0) out.forbiddenMcpServers = forbiddenMcpServers;
  const managedPlugins = csvToArr(value.managedPlugins);
  if (managedPlugins.length > 0) out.managedPlugins = managedPlugins;
  const forbiddenSkills = csvToArr(value.forbiddenSkills);
  if (forbiddenSkills.length > 0) out.forbiddenSkills = forbiddenSkills;
  if (value.managedSkills.trim() === "all") {
    out.managedSkills = "all";
  } else {
    const managedSkills = csvToArr(value.managedSkills);
    if (managedSkills.length > 0) out.managedSkills = managedSkills;
  }
  for (const [field, raw] of [
    ["skillPolicy", value.skillPolicyJson],
    ["sandbox", value.sandboxJson],
    ["hooks", value.hooksJson],
    ["mcpServers", value.mcpServersJson],
  ] as const) {
    if (!raw.trim()) continue;
    try {
      out[field] = JSON.parse(raw) as unknown;
    } catch (err) {
      return {
        ok: false,
        error: `${field} JSON 解析失败：${err instanceof Error ? err.message : String(err)}`,
      };
    }
  }
  // managedSettings = 安全余项 JSON + 结构化预算/模型策略字段合回一个对象
  const ms: Record<string, unknown> = {};
  if (value.managedSettingsJson.trim()) {
    try {
      const rest = JSON.parse(value.managedSettingsJson) as unknown;
      if (rest !== null && typeof rest === "object" && !Array.isArray(rest)) {
        if ("availableModels" in (rest as Record<string, unknown>)) {
          return {
            ok: false,
            error:
              "availableModels 已退役，模型治理请改用「模型圈定」区（三层漏斗白名单）",
          };
        }
        if ("disabledModels" in (rest as Record<string, unknown>)) {
          return {
            ok: false,
            error:
              "disabledModels 已退役（2026-07-17 enforcement 换血），模型治理请改用「模型圈定」区（三层漏斗白名单）",
          };
        }
        Object.assign(ms, rest as Record<string, unknown>);
      } else {
        return { ok: false, error: "managedSettings JSON 必须是对象" };
      }
    } catch (err) {
      return {
        ok: false,
        error: `managedSettings JSON 解析失败：${err instanceof Error ? err.message : String(err)}`,
      };
    }
  }
  const budget = parsePositiveUsd(value.maxAllowedBudgetUsd, "单会话预算上限");
  if (budget.error) return { ok: false, error: budget.error };
  if (budget.value !== undefined) ms.maxAllowedBudgetUsd = budget.value;
  const cloudBudget = parsePositiveUsd(value.cloudTaskDailyBudgetUsd, "每员工每日云任务告警额度");
  if (cloudBudget.error) return { ok: false, error: cloudBudget.error };
  if (cloudBudget.value !== undefined) ms.cloudTaskDailyBudgetUsd = cloudBudget.value;
  if (value.maxAllowedEffort.trim()) ms.maxAllowedEffort = value.maxAllowedEffort.trim();
  const visibleGroups = csvToArr(value.visibleGroups);
  if (visibleGroups.length > 0) ms.visibleGroups = visibleGroups;
  if (value.lockRoleMapping) ms.lockRoleMapping = true;
  // 缺省开：只在「关闭」时落 false（收严语义单源，与 server buildEmployeeManagedSettings 对齐）。
  if (!value.orgMemoryEnabled) ms.orgMemoryEnabled = false;
  // 缺省给：只在「收走」时落 false（同 orgMemoryEnabled 姿态，见 shared types 字段注释）。
  if (!value.memoryReadToggleAllowed) ms.memoryReadToggleAllowed = false;
  // OPT-2 权限层：三数组 JSON 直编 + defaultMode/disableBypass + extra passthrough →
  // 唯一构建器 buildManagedPermissions 校验合回 ms.permissions（校验/解析失败 inline amber）。
  let permInput: Record<string, unknown>;
  try {
    permInput = {
      ...parsePermExtra(value.permissionsExtraJson),
      allow: parsePermArray(value.permissionsAllow, "允许"),
      ask: parsePermArray(value.permissionsAsk, "询问"),
      deny: parsePermArray(value.permissionsDeny, "禁止"),
      defaultMode: value.permissionsDefaultMode.trim() || undefined,
      disableBypassPermissionsMode: value.permissionsDisableBypass
        ? "disable"
        : undefined,
    };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
  try {
    const permissions = buildManagedPermissions(permInput);
    if (permissions) ms.permissions = permissions;
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
  // OPT-6 连接器三档：唯一构建器 buildConnectorTiers「空即省略」——只在关闭某档时落
  // connectors[x]=false（policy 层只收不放）。agent 自助接入模式非 auto 时落字段。
  const connectors = buildConnectorTiers({
    remote: value.connectorRemote,
    npm: value.connectorNpm,
    command: value.connectorCommand,
  });
  if (connectors) ms.connectors = connectors;
  if (value.agentConnectorMode && value.agentConnectorMode !== "auto") {
    ms.agentConnectorMode = value.agentConnectorMode;
  }
  if (Object.keys(ms).length > 0) out.managedSettings = ms;
  // G-K5 员工日/周额度（顶层 dosia.memberQuota，非 managedSettings 子字段）。
  const memberQuota: { daily?: number; weekly?: number } = {};
  const daily = parsePositiveUsd(value.memberQuotaDaily, "员工日额度");
  if (daily.error) return { ok: false, error: daily.error };
  if (daily.value !== undefined) memberQuota.daily = daily.value;
  const weekly = parsePositiveUsd(value.memberQuotaWeekly, "员工周额度");
  if (weekly.error) return { ok: false, error: weekly.error };
  if (weekly.value !== undefined) memberQuota.weekly = weekly.value;
  if (Object.keys(memberQuota).length > 0) out.memberQuota = memberQuota;
  if (Object.keys(value.agentRoleBindings).length > 0) {
    out.agentRoleBindings = value.agentRoleBindings;
  }
  // dosia.modelSelection（角色可见性 + 每模型参数）在上方 base 透传阶段已整体原样带过（不在
  // MANAGED_DOSIA_KEYS），本编辑器不重建也不抹改——统一「模型治理」面（scope=角色）是唯一编辑者
  // （P2c 收口：旧 RoleModelCurationField/buildModelSelection 双写路径已移除，红线 #9）。
  // F4 · Codex 委派（顶层 dosia.delegateCodex）——关闭时省略字段（undefined = 消费端
  // normalizeDelegateCodexConfig 默认关，与其它「留空/关闭即省略」字段同姿态；开启时逐字段
  // 独立取值，各自缺省时省略子字段，让 normalizeDelegateCodexConfig 落自己的默认）。
  if (value.delegateCodexEnabled) {
    const dc: Record<string, unknown> = { enabled: true };
    if (value.delegateCodexMaxSandbox === "read-only") dc.maxSandbox = "read-only";
    if (value.delegateCodexReviewGate) dc.reviewGate = true;
    if (
      value.delegateCodexChannelMode === "enterprise" ||
      value.delegateCodexChannelMode === "personal-first"
    ) {
      dc.channelMode = value.delegateCodexChannelMode;
    }
    if (value.delegateCodexDefaultModel.trim()) {
      dc.defaultModel = value.delegateCodexDefaultModel.trim();
    }
    out.delegateCodex = dc;
  } else {
    delete out.delegateCodex;
  }
  return { ok: true, dosia: out };
}

/** 解析正数美元输入：空 → {}（跳过），非正数 → {error}，正数 → {value}。
 *  单会话预算 + 员工日/周额度三处共用，统一「留空=不设 · 必须正数」语义。 */
function parsePositiveUsd(raw: string, label: string): { value?: number; error?: string } {
  if (!raw.trim()) return {};
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return { error: `${label}必须是正数（美元）` };
  return { value: n };
}

function arrToCsv(v: unknown): string {
  if (!Array.isArray(v)) return "";
  return v.filter((x) => typeof x === "string").join(", ");
}

/**
 * managedSettings 拆分 hydrate（2026-07-03 成本/模型策略下放）：
 * 白名单子字段抽成结构化表单值，managedSettingsJson 只剩安全类余项
 * （permissions/env/…）—— 同一对象单一表示不打架；serialize 再合回一个对象。
 */
function hydrateManagedSettings(ms: unknown): {
  maxAllowedBudgetUsd: string;
  cloudTaskDailyBudgetUsd: string;
  maxAllowedEffort: string;
  visibleGroups: string;
  lockRoleMapping: boolean;
  orgMemoryEnabled: boolean;
  memoryReadToggleAllowed: boolean;
  permissionsAllow: string;
  permissionsAsk: string;
  permissionsDeny: string;
  permissionsDefaultMode: string;
  permissionsDisableBypass: boolean;
  permissionsExtraJson: string;
  connectorRemote: boolean;
  connectorNpm: boolean;
  connectorCommand: boolean;
  agentConnectorMode: string;
  managedSettingsJson: string;
} {
  const obj =
    ms !== null && typeof ms === "object" && !Array.isArray(ms)
      ? (ms as Record<string, unknown>)
      : undefined;
  const editable = new Set<string>(ORG_ADMIN_EDITABLE_MANAGED_SETTINGS_KEYS);
  const rest: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj ?? {})) {
    if (!editable.has(k)) rest[k] = v;
  }
  return {
    maxAllowedBudgetUsd:
      typeof obj?.maxAllowedBudgetUsd === "number"
        ? String(obj.maxAllowedBudgetUsd)
        : "",
    cloudTaskDailyBudgetUsd:
      typeof obj?.cloudTaskDailyBudgetUsd === "number" ? String(obj.cloudTaskDailyBudgetUsd) : "",
    maxAllowedEffort:
      typeof obj?.maxAllowedEffort === "string" ? obj.maxAllowedEffort : "",
    visibleGroups: arrToCsv(obj?.visibleGroups),
    lockRoleMapping: obj?.lockRoleMapping === true,
    // 缺省（undefined）/ true → 勾选（开）；仅显式 false → 取消（关）。
    orgMemoryEnabled: obj?.orgMemoryEnabled !== false,
    memoryReadToggleAllowed: obj?.memoryReadToggleAllowed !== false,
    ...hydratePermissions(obj?.permissions),
    // 连接器三档：缺省（undefined）/ true → 勾选（开）；仅显式 false → 取消（关）。
    connectorRemote: (obj?.connectors as { remote?: boolean } | undefined)?.remote !== false,
    connectorNpm: (obj?.connectors as { npm?: boolean } | undefined)?.npm !== false,
    connectorCommand: (obj?.connectors as { command?: boolean } | undefined)?.command !== false,
    agentConnectorMode:
      typeof obj?.agentConnectorMode === "string" ? obj.agentConnectorMode : "auto",
    managedSettingsJson:
      Object.keys(rest).length > 0 ? JSON.stringify(rest, null, 2) : "",
  };
}

/** permissions 卡片建模的子字段（三数组 + defaultMode + disableBypass）；识别不了的
 *  子字段（additionalDirectories / 未来 SDK 新键）落 permissionsExtraJson passthrough。 */
const PERMISSION_KNOWN_SUBKEYS = new Set([
  "allow",
  "ask",
  "deny",
  "defaultMode",
  "disableBypassPermissionsMode",
]);

/** managedSettings.permissions → 结构化表单值。三数组以 pretty JSON 文本回填
 *  （高级区可直编，工具三态/白话开关是其派生视图）。认不出的规则原样留在 JSON 里不丢。 */
function hydratePermissions(p: unknown): {
  permissionsAllow: string;
  permissionsAsk: string;
  permissionsDeny: string;
  permissionsDefaultMode: string;
  permissionsDisableBypass: boolean;
  permissionsExtraJson: string;
} {
  const obj =
    p !== null && typeof p === "object" && !Array.isArray(p)
      ? (p as Record<string, unknown>)
      : undefined;
  const extra: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj ?? {})) {
    if (!PERMISSION_KNOWN_SUBKEYS.has(k)) extra[k] = v;
  }
  return {
    permissionsAllow: strArrToJson(obj?.allow),
    permissionsAsk: strArrToJson(obj?.ask),
    permissionsDeny: strArrToJson(obj?.deny),
    permissionsDefaultMode:
      typeof obj?.defaultMode === "string" ? obj.defaultMode : "",
    permissionsDisableBypass: obj?.disableBypassPermissionsMode === "disable",
    permissionsExtraJson:
      Object.keys(extra).length > 0 ? JSON.stringify(extra, null, 2) : "",
  };
}

/** 字符串数组 → pretty JSON 文本（空/非数组 → ""，让 serialize 跳过）。 */
function strArrToJson(v: unknown): string {
  if (!Array.isArray(v)) return "";
  const arr = v.filter((x): x is string => typeof x === "string");
  return arr.length > 0 ? JSON.stringify(arr, null, 2) : "";
}

/** G-K5：顶层 dosia.memberQuota → 两个日/周额度表单字段（USD，缺 = 空 = 无限）。 */
function hydrateMemberQuota(mq: unknown): { memberQuotaDaily: string; memberQuotaWeekly: string } {
  const obj = mq !== null && typeof mq === "object" && !Array.isArray(mq) ? (mq as Record<string, unknown>) : undefined;
  return {
    memberQuotaDaily: typeof obj?.daily === "number" && obj.daily > 0 ? String(obj.daily) : "",
    memberQuotaWeekly: typeof obj?.weekly === "number" && obj.weekly > 0 ? String(obj.weekly) : "",
  };
}

/** F4 · Codex 委派表单值 hydrate——唯一归一化 normalizeDelegateCodexConfig（boolean 简写 /
 *  全字段对象 / 脏值统一收敛），禁散落 `=== true` 判断（红线 #9 单一真相源）。 */
function hydrateDelegateCodex(dc: unknown): {
  delegateCodexEnabled: boolean;
  delegateCodexChannelMode: string;
  delegateCodexMaxSandbox: string;
  delegateCodexReviewGate: boolean;
  delegateCodexDefaultModel: string;
} {
  const norm = normalizeDelegateCodexConfig(dc);
  return {
    delegateCodexEnabled: norm.enabled,
    delegateCodexChannelMode: norm.channelMode,
    delegateCodexMaxSandbox: norm.maxSandbox,
    delegateCodexReviewGate: norm.reviewGate,
    delegateCodexDefaultModel: norm.defaultModel ?? "",
  };
}

function hydrateBindings(v: unknown): Record<string, string[]> {
  if (v === null || typeof v !== "object" || Array.isArray(v)) return {};
  const out: Record<string, string[]> = {};
  for (const [k, roles] of Object.entries(v as Record<string, unknown>)) {
    if (Array.isArray(roles)) {
      out[k] = roles.filter((r): r is string => typeof r === "string");
    }
  }
  return out;
}

/**
 * 内置 Agent 可见性（agentRoleBindings，2026-07-03 下放企业侧）。
 *
 * 语义：员工客户端用**自己角色 config 里的 bindings** 过滤内置 Agent
 * （resolveAgentRoles：override ?? 产品默认；[] = 全员可见），所以本编辑器只回答
 * 一个问题——「本角色的员工可不可见该 Agent」，编码规则自洽于本角色 config：
 *  - 勾选且产品默认已可见 → 删除 override（保持配置干净）
 *  - 勾选但默认不可见 → override = 默认列表 + 本 templateId
 *  - 取消且默认就不可见 → 删除 override
 *  - 取消但默认可见 → override = 默认列表去掉本 templateId；若结果为空
 *    （默认全员/仅本角色）→ ["admin"]（管理员本就 bypass，语义=对本角色隐藏）
 */
function AgentVisibilityField({
  templateId,
  bindings,
  onChange,
}: {
  templateId: string;
  bindings: Record<string, string[]>;
  onChange: (next: Record<string, string[]>) => void;
}): JSX.Element {
  const tid = templateId.trim();
  const setVisible = (agentId: string, defaultRoles: readonly string[], visible: boolean): void => {
    const next = { ...bindings };
    const defaultVisible = defaultRoles.length === 0 || defaultRoles.includes(tid);
    if (visible === defaultVisible) {
      delete next[agentId];
    } else if (visible) {
      next[agentId] = [...defaultRoles, tid];
    } else {
      const rest = defaultRoles.filter((r) => r !== tid);
      next[agentId] = rest.length > 0 ? rest : [ADMIN_ROLE_TEMPLATE_ID];
    }
    onChange(next);
  };
  return (
    <FormField
      label="内置 Agent 可见性"
      hint={
        tid
          ? "勾选 = 本角色员工的工作台可见该内置 Agent（管理员始终全可见）"
          : "先在上方填写 templateId 再配置可见性"
      }
    >
      <div className="rounded-md border border-dosia-border p-2 flex flex-col gap-1.5">
        {BUILTIN_AGENT_ROLE_DEFAULTS.map((agent) => {
          const effective = bindings[agent.id] ?? agent.defaultRoles;
          const visible = effective.length === 0 || (tid !== "" && effective.includes(tid));
          const isDefault = bindings[agent.id] === undefined;
          return (
            <label
              key={agent.id}
              className="flex items-center gap-2 cursor-pointer text-sm text-dosia-text"
            >
              <input
                type="checkbox"
                checked={visible}
                disabled={tid === ""}
                onChange={(e) => setVisible(agent.id, agent.defaultRoles, e.target.checked)}
                aria-label={`agent-visible-${agent.id}`}
              />
              <span>{agent.name}</span>
              <span className="text-xs text-dosia-text-dim">
                默认{agent.defaultRoles.length === 0 ? "全员" : agent.defaultRoles.join("/")}
                {isDefault ? "" : " · 已覆盖"}
              </span>
            </label>
          );
        })}
      </div>
    </FormField>
  );
}

// ── 结构化 MCP servers 编辑器（底层仍序列化成 mcpServersJson 字符串） ──

interface McpRow {
  name: string;
  type: "stdio" | "http" | "sse";
  command: string;
  args: string;
  url: string;
  /** stdio→env / http·sse→headers，"KEY=value" 每行一条 */
  kv: string;
  description: string;
}

function kvToRecord(kv: string): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const line of kv.split("\n")) {
    const t = line.trim();
    if (!t) continue;
    const eq = t.indexOf("=");
    if (eq <= 0) continue;
    out[t.slice(0, eq).trim()] = t.slice(eq + 1).trim();
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function recordToKv(r: unknown): string {
  if (!r || typeof r !== "object") return "";
  return Object.entries(r as Record<string, unknown>)
    .map(([k, v]) => `${k}=${typeof v === "string" ? v : String(v)}`)
    .join("\n");
}

/** mcpServersJson → 行列表（解析失败返回 null，调用方回退裸 JSON）。 */
function parseMcpRows(json: string): McpRow[] | null {
  if (!json.trim()) return [];
  let obj: unknown;
  try { obj = JSON.parse(json); } catch { return null; }
  if (!obj || typeof obj !== "object") return null;
  return Object.entries(obj as Record<string, Record<string, unknown>>).map(([name, e]) => {
    const type = e.type === "http" || e.type === "sse" ? e.type : "stdio";
    return {
      name,
      type,
      command: typeof e.command === "string" ? e.command : "",
      args: Array.isArray(e.args) ? e.args.filter((x): x is string => typeof x === "string").join(" ") : "",
      url: typeof e.url === "string" ? e.url : "",
      kv: recordToKv(type === "stdio" ? e.env : e.headers),
      description: typeof e.description === "string" ? e.description : "",
    };
  });
}

/** 行列表 → mcpServersJson 字符串（空列表 → ""，让 serialize 跳过该字段）。 */
function serializeMcpRows(rows: McpRow[]): string {
  const out: Record<string, Record<string, unknown>> = {};
  for (const r of rows) {
    const name = r.name.trim();
    if (!name) continue;
    const entry: Record<string, unknown> = { name, type: r.type, enabled: true };
    if (r.type === "stdio") {
      if (r.command.trim()) entry.command = r.command.trim();
      const args = r.args.trim().split(/\s+/).filter(Boolean);
      if (args.length > 0) entry.args = args;
      const env = kvToRecord(r.kv);
      if (env) entry.env = env;
    } else {
      if (r.url.trim()) entry.url = r.url.trim();
      const headers = kvToRecord(r.kv);
      if (headers) entry.headers = headers;
    }
    if (r.description.trim()) entry.description = r.description.trim();
    out[name] = entry;
  }
  return Object.keys(out).length > 0 ? JSON.stringify(out, null, 2) : "";
}

function McpServersField({
  value,
  onChange,
}: {
  value: string;
  onChange: (json: string) => void;
}): JSX.Element {
  const rows = parseMcpRows(value);
  // 手写的非法 JSON → 回退裸 textarea，别让结构化编辑器吞掉内容。
  if (rows === null) {
    return (
      <FormField label="mcpServers (JSON 解析失败 · 裸编辑)" hint="修正 JSON 后会自动切回表单">
        <TextArea rows={6} value={value} onChange={(e) => onChange(e.target.value)} placeholder="{}" />
      </FormField>
    );
  }
  const setRow = (i: number, p: Partial<McpRow>): void =>
    onChange(serializeMcpRows(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r))));
  const addRow = (): void =>
    onChange(serializeMcpRows([...rows, { name: "", type: "stdio", command: "", args: "", url: "", kv: "", description: "" }]));
  const removeRow = (i: number): void => onChange(serializeMcpRows(rows.filter((_, idx) => idx !== i)));

  return (
    <FormField
      label="预配 MCP servers"
      hint="为该角色预配自建 MCP（stdio/http），登录合进员工 workspace 标 managed 只读"
    >
      <div className="flex flex-col gap-2">
        {rows.length === 0 && <p className="text-xs text-dosia-text-dim">暂无 · 点下方添加</p>}
        {rows.map((r, i) => (
          <div key={i} className="rounded-md border border-dosia-border p-2 flex flex-col gap-1.5">
            <div className="grid grid-cols-3 gap-2">
              <TextInput value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} placeholder="名称 (my-api)" aria-label="mcp-name" />
              <select
                value={r.type}
                onChange={(e) => setRow(i, { type: e.target.value as McpRow["type"] })}
                className="rounded-md border border-dosia-border bg-transparent px-2 py-1 text-sm"
                aria-label="mcp-type"
              >
                <option value="stdio">stdio</option>
                <option value="http">http</option>
                <option value="sse">sse</option>
              </select>
              <button type="button" onClick={() => removeRow(i)} className="text-xs text-dosia-danger hover:underline justify-self-end self-center">删除</button>
            </div>
            {r.type === "stdio" ? (
              <div className="grid grid-cols-2 gap-2">
                <TextInput value={r.command} onChange={(e) => setRow(i, { command: e.target.value })} placeholder="命令 (npx)" aria-label="mcp-command" />
                <TextInput value={r.args} onChange={(e) => setRow(i, { args: e.target.value })} placeholder="参数 (空格分隔)" aria-label="mcp-args" />
              </div>
            ) : (
              <TextInput value={r.url} onChange={(e) => setRow(i, { url: e.target.value })} placeholder="URL (https://...)" aria-label="mcp-url" />
            )}
            <TextArea rows={2} value={r.kv} onChange={(e) => setRow(i, { kv: e.target.value })} placeholder={r.type === "stdio" ? "环境变量 KEY=value（每行一条）" : "Headers KEY=value（每行一条）"} />
            <TextInput value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} placeholder="描述（可选）" aria-label="mcp-desc" />
          </div>
        ))}
        <button type="button" onClick={addRow} className="text-xs text-dosia-accent hover:underline self-start">+ 添加 MCP</button>
      </div>
    </FormField>
  );
}

function csvToArr(v: string): string[] {
  return v
    .split(",")
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
}

/** permissions 三数组 JSON 文本 → unknown（空 → []）；解析失败抛友好错。元素合法性由
 *  buildManagedPermissions 校验，这里只负责把 JSON 文本变回值。 */
function parsePermArray(raw: string, label: string): unknown {
  if (!raw.trim()) return [];
  try {
    return JSON.parse(raw);
  } catch (err) {
    throw new Error(
      `权限「${label}」JSON 解析失败：${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/** permissions extra passthrough JSON → 对象（空 → {}）；非对象/解析失败抛友好错。 */
function parsePermExtra(raw: string): Record<string, unknown> {
  if (!raw.trim()) return {};
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch (err) {
    throw new Error(
      `permissions 其他子字段 JSON 解析失败：${err instanceof Error ? err.message : String(err)}`,
    );
  }
  if (obj === null || typeof obj !== "object" || Array.isArray(obj)) {
    throw new Error("permissions 其他子字段必须是 JSON 对象");
  }
  return obj as Record<string, unknown>;
}

// ── OPT-2 权限层 · 工具权限结构化卡片（简化面板 + 折叠高级区，两层同一份数据） ──

/** 工具三态行覆盖的常用工具集（bare 工具名规则 = 对该工具整体放行/询问/禁止）。 */
const PERMISSION_TOOLS: { name: string; label: string }[] = [
  { name: "Write", label: "Write（文件写入）" },
  { name: "Edit", label: "Edit（文件编辑）" },
  { name: "Bash", label: "Bash（终端命令）" },
  { name: "WebSearch", label: "WebSearch（联网搜索）" },
  { name: "WebFetch", label: "WebFetch（拉取网页）" },
];

type PermState = "allow" | "ask" | "deny" | "default";

const PERMISSION_STATES: { v: PermState; label: string }[] = [
  { v: "allow", label: "允许" },
  { v: "ask", label: "询问" },
  { v: "deny", label: "禁止" },
  { v: "default", label: "默认" },
];

/** 白话开关：一次操作若干条具体命令规则进/出某个数组。 */
const PERMISSION_TOGGLES: {
  key: string;
  label: string;
  hint: string;
  target: "allow" | "ask" | "deny";
  rules: string[];
}[] = [
  {
    key: "noGitPush",
    label: "禁止推送代码",
    hint: "Bash(git push:*) → 禁止",
    target: "deny",
    rules: ["Bash(git push:*)"],
  },
  {
    key: "autoInstall",
    label: "装依赖免询问",
    hint: "Bash(pnpm install:*) / Bash(npm install:*) → 允许",
    target: "allow",
    rules: ["Bash(pnpm install:*)", "Bash(npm install:*)"],
  },
  {
    key: "autoTest",
    label: "跑测试免询问",
    hint: "Bash(pnpm test:*) → 允许",
    target: "allow",
    rules: ["Bash(pnpm test:*)"],
  },
  {
    key: "askDownload",
    label: "下载文件要询问",
    hint: "Bash(curl:*) / Bash(wget:*) → 询问",
    target: "ask",
    rules: ["Bash(curl:*)", "Bash(wget:*)"],
  },
];

/** JSON 文本 → 字符串数组（空 → []，非法 JSON/非数组 → null，调用方回退裸编辑）。 */
function safePermArray(text: string): string[] | null {
  if (!text.trim()) return [];
  let v: unknown;
  try {
    v = JSON.parse(text);
  } catch {
    return null;
  }
  if (!Array.isArray(v)) return null;
  return v.filter((x): x is string => typeof x === "string");
}

/** 字符串数组 → JSON 文本（空 → ""，让 serialize 跳过该数组）。去重保序。 */
function permArrToText(arr: string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of arr) {
    if (seen.has(r)) continue;
    seen.add(r);
    out.push(r);
  }
  return out.length > 0 ? JSON.stringify(out, null, 2) : "";
}

function PermissionsField({
  value,
  patch,
}: {
  value: PolicyEditorValue;
  patch: (p: Partial<PolicyEditorValue>) => void;
}): JSX.Element {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const allow = safePermArray(value.permissionsAllow);
  const ask = safePermArray(value.permissionsAsk);
  const deny = safePermArray(value.permissionsDeny);
  const parsedOk = allow !== null && ask !== null && deny !== null;

  const writeArrays = (a: string[], k: string[], d: string[]): void =>
    patch({
      permissionsAllow: permArrToText(a),
      permissionsAsk: permArrToText(k),
      permissionsDeny: permArrToText(d),
    });

  const toolState = (tool: string): PermState => {
    if (deny?.includes(tool)) return "deny";
    if (ask?.includes(tool)) return "ask";
    if (allow?.includes(tool)) return "allow";
    return "default";
  };
  const setToolState = (tool: string, state: PermState): void => {
    if (!allow || !ask || !deny) return;
    const strip = (arr: string[]): string[] => arr.filter((r) => r !== tool);
    const a = strip(allow);
    const k = strip(ask);
    const d = strip(deny);
    if (state === "allow") a.push(tool);
    else if (state === "ask") k.push(tool);
    else if (state === "deny") d.push(tool);
    writeArrays(a, k, d);
  };

  const toggleOn = (t: (typeof PERMISSION_TOGGLES)[number]): boolean => {
    const arr =
      t.target === "allow" ? allow : t.target === "ask" ? ask : deny;
    return !!arr && t.rules.every((r) => arr.includes(r));
  };
  const setToggle = (
    t: (typeof PERMISSION_TOGGLES)[number],
    on: boolean,
  ): void => {
    if (!allow || !ask || !deny) return;
    const arrs = { allow: [...allow], ask: [...ask], deny: [...deny] };
    const cur = arrs[t.target];
    arrs[t.target] = on
      ? [...cur, ...t.rules.filter((r) => !cur.includes(r))]
      : cur.filter((r) => !t.rules.includes(r));
    writeArrays(arrs.allow, arrs.ask, arrs.deny);
  };

  return (
    <div className="rounded-md border border-dosia-border p-3 flex flex-col gap-3">
      <p className="text-xs font-medium text-dosia-text">
        工具权限
        <span className="ml-2 font-normal text-dosia-text-dim">
          组织自治项 · 该角色员工机器上工具/命令的放行·询问·禁止（危险命令 74 条黑名单地板恒生效兜底）
        </span>
      </p>

      {parsedOk ? (
        <>
          {/* 工具三态行 */}
          <div className="flex flex-col gap-1.5">
            {PERMISSION_TOOLS.map((tool) => {
              const cur = toolState(tool.name);
              return (
                <div key={tool.name} className="flex items-center justify-between gap-2">
                  <span className="text-sm text-dosia-text">{tool.label}</span>
                  <div className="flex rounded-md border border-dosia-border overflow-hidden">
                    {PERMISSION_STATES.map((st) => (
                      <button
                        key={st.v}
                        type="button"
                        onClick={() => setToolState(tool.name, st.v)}
                        aria-label={`perm-${tool.name}-${st.v}`}
                        aria-pressed={cur === st.v}
                        className={
                          "px-2 py-1 text-xs " +
                          (cur === st.v
                            ? "bg-dosia-accent text-white"
                            : "text-dosia-text-secondary hover:bg-dosia-hover")
                        }
                      >
                        {st.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 白话开关 */}
          <div className="flex flex-col gap-1.5 border-t border-dosia-border pt-2">
            {PERMISSION_TOGGLES.map((t) => (
              <label
                key={t.key}
                className="flex items-center gap-2 cursor-pointer text-sm text-dosia-text"
              >
                <input
                  type="checkbox"
                  checked={toggleOn(t)}
                  onChange={(e) => setToggle(t, e.target.checked)}
                  aria-label={`perm-toggle-${t.key}`}
                />
                <span>{t.label}</span>
                <span className="text-xs text-dosia-text-dim">{t.hint}</span>
              </label>
            ))}
          </div>
        </>
      ) : (
        <p className="text-xs text-amber-700" role="alert">
          allow/ask/deny 高级 JSON 解析失败 —— 修正下方 JSON 后恢复结构化控件。
        </p>
      )}

      {/* defaultMode + disableBypass */}
      <div className="grid grid-cols-2 gap-3 border-t border-dosia-border pt-2">
        <FormField label="默认权限模式 (defaultMode)" hint="留空 = 不下发（沿用 DOSIA 默认）">
          <select
            value={value.permissionsDefaultMode}
            onChange={(e) => patch({ permissionsDefaultMode: e.target.value })}
            className="w-full rounded-md border border-dosia-border bg-dosia-bg-input px-2 py-1.5 text-sm text-dosia-text"
            aria-label="permissionsDefaultMode"
          >
            <option value="">默认（不下发）</option>
            {MANAGED_PERMISSION_MODES.map((m) => (
              <option key={m} value={m}>
                {m}
                {m === "bypassPermissions" ? "（全自动 · 慎用）" : ""}
              </option>
            ))}
          </select>
        </FormField>
        <label className="flex items-center gap-2 cursor-pointer self-end pb-1.5">
          <input
            type="checkbox"
            checked={value.permissionsDisableBypass}
            onChange={(e) => patch({ permissionsDisableBypass: e.target.checked })}
            aria-label="permissionsDisableBypass"
          />
          <span className="text-sm text-dosia-text">
            禁用全自动档（禁止员工切到 bypassPermissions 全自动模式）
          </span>
        </label>
      </div>

      <button
        type="button"
        onClick={() => setShowAdvanced((x) => !x)}
        className="text-xs text-dosia-accent hover:underline self-start"
      >
        {showAdvanced ? "收起高级（规则直编）" : "展开高级（allow / ask / deny 规则直编）"}
      </button>
      {showAdvanced ? (
        <div className="flex flex-col gap-2 border-t border-dosia-border pt-2">
          <FormField label="allow (JSON 数组 · 静默放行)" hint='["Bash(pnpm test:*)"]'>
            <TextArea
              rows={3}
              value={value.permissionsAllow}
              onChange={(e) => patch({ permissionsAllow: e.target.value })}
              placeholder="[]"
            />
          </FormField>
          <FormField label="ask (JSON 数组 · 每次询问)" hint='["Bash(curl:*)"]'>
            <TextArea
              rows={3}
              value={value.permissionsAsk}
              onChange={(e) => patch({ permissionsAsk: e.target.value })}
              placeholder="[]"
            />
          </FormField>
          <FormField label="deny (JSON 数组 · 连 bypass 都拦)" hint='["Bash(git push:*)"]'>
            <TextArea
              rows={3}
              value={value.permissionsDeny}
              onChange={(e) => patch({ permissionsDeny: e.target.value })}
              placeholder="[]"
            />
          </FormField>
          {value.permissionsExtraJson.trim() ? (
            <FormField
              label="permissions 其他子字段 (JSON)"
              hint="additionalDirectories 等本卡片未建模的子字段，原样保留"
            >
              <TextArea
                rows={3}
                value={value.permissionsExtraJson}
                onChange={(e) => patch({ permissionsExtraJson: e.target.value })}
                placeholder="{}"
              />
            </FormField>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ── OPT-6 开放连接 · 连接器档位卡片（结构化，镜像 OPT-2「工具权限」卡片模式）──

const CONNECTOR_TIER_ROWS: Array<{
  key: "connectorRemote" | "connectorNpm" | "connectorCommand";
  label: string;
  hint: string;
}> = [
  { key: "connectorRemote", label: "远程接口（http/sse）", hint: "url + 鉴权头，零本机依赖，最安全" },
  { key: "connectorNpm", label: "npm 包型（npx）", hint: "员工机首启自动拉包" },
  { key: "connectorCommand", label: "任意本地命令", hint: "风险最高；无论谁开都带风险横幅+命令确认卡（地板）" },
];

const AGENT_CONNECTOR_MODE_LABEL: Record<string, string> = {
  auto: "全自动（agent 自建自测自激活，事后通知）",
  confirm: "需审核（落库前用户点头）",
  off: "关闭（agent 不得经对话自建；员工仍可手动加）",
};

function ConnectorTiersField({
  value,
  patch,
}: {
  value: PolicyEditorValue;
  patch: (p: Partial<PolicyEditorValue>) => void;
}): JSX.Element {
  return (
    <div className="rounded-md border border-dosia-border p-3 flex flex-col gap-3">
      <p className="text-xs font-medium text-dosia-text">
        连接器档位（开放连接）
        <span className="ml-2 font-normal text-dosia-text-dim">
          组织自治项 · 该角色员工能自建哪类连接器、agent 能否全自动接入（只收不放 · 命令型确认地板恒生效）
        </span>
      </p>

      <div className="flex flex-col gap-1.5">
        {CONNECTOR_TIER_ROWS.map((row) => (
          <label
            key={row.key}
            className="flex items-center gap-2 cursor-pointer text-sm text-dosia-text"
          >
            <input
              type="checkbox"
              checked={value[row.key]}
              onChange={(e) => patch({ [row.key]: e.target.checked } as Partial<PolicyEditorValue>)}
              aria-label={row.key}
            />
            <span>{row.label}</span>
            <span className="text-xs text-dosia-text-dim">{row.hint}</span>
          </label>
        ))}
      </div>

      <FormField
        label="agent 自助接入模式 (agentConnectorMode)"
        hint="任意命令型无视此项，恒需用户点头（地板）"
      >
        <select
          value={value.agentConnectorMode || "auto"}
          onChange={(e) => patch({ agentConnectorMode: e.target.value })}
          className="w-full rounded-md border border-dosia-border bg-dosia-bg-input px-2 py-1.5 text-sm text-dosia-text"
          aria-label="agentConnectorMode"
        >
          {AGENT_CONNECTOR_MODES.map((m) => (
            <option key={m} value={m}>
              {AGENT_CONNECTOR_MODE_LABEL[m] ?? m}
            </option>
          ))}
        </select>
      </FormField>
    </div>
  );
}

// ── F4 · Codex 委派卡片（结构化，镜像 OPT-6「连接器档位」卡片模式）──

const DELEGATE_CODEX_CHANNEL_MODES: Array<"personal" | "enterprise" | "personal-first"> = [
  "personal",
  "personal-first",
  "enterprise",
];

const DELEGATE_CODEX_CHANNEL_MODE_LABEL: Record<string, string> = {
  personal: "个人（走员工本机 ~/.codex 个人登录态 · ChatGPT 订阅额度）",
  "personal-first": "个人优先（有个人登录态用个人，否则回落企业）",
  enterprise: "企业（免登录 · 走企业网关计费）",
};

const DELEGATE_CODEX_MAX_SANDBOX: Array<"workspace-write" | "read-only"> = [
  "workspace-write",
  "read-only",
];

const DELEGATE_CODEX_MAX_SANDBOX_LABEL: Record<string, string> = {
  "workspace-write": "允许改盘（缺省）",
  "read-only": "强制只读（即便主脑传 readOnly:false 也收窄）",
};

function DelegateCodexField({
  value,
  patch,
}: {
  value: PolicyEditorValue;
  patch: (p: Partial<PolicyEditorValue>) => void;
}): JSX.Element {
  return (
    <div className="rounded-md border border-dosia-border p-3 flex flex-col gap-3">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={value.delegateCodexEnabled}
          onChange={(e) => patch({ delegateCodexEnabled: e.target.checked })}
          aria-label="delegateCodexEnabled"
        />
        <span className="text-xs font-medium text-dosia-text">
          Codex 委派（delegate_codex）
          <span className="ml-2 font-normal text-dosia-text-dim">
            允许本角色员工把编码任务委派给 Codex 子代理执行
          </span>
        </span>
      </label>

      {value.delegateCodexEnabled ? (
        <div className="grid grid-cols-2 gap-3">
          <FormField label="委派通道 (channelMode)" hint="免登录企业通道走企业网关计费">
            <select
              value={value.delegateCodexChannelMode || "personal"}
              onChange={(e) => patch({ delegateCodexChannelMode: e.target.value })}
              className="w-full rounded-md border border-dosia-border bg-dosia-bg-input px-2 py-1.5 text-sm text-dosia-text"
              aria-label="delegateCodexChannelMode"
            >
              {DELEGATE_CODEX_CHANNEL_MODES.map((m) => (
                <option key={m} value={m}>
                  {DELEGATE_CODEX_CHANNEL_MODE_LABEL[m] ?? m}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="沙箱权限地板 (maxSandbox)" hint="委派给 Codex 的最大改盘权限">
            <select
              value={value.delegateCodexMaxSandbox || "workspace-write"}
              onChange={(e) => patch({ delegateCodexMaxSandbox: e.target.value })}
              className="w-full rounded-md border border-dosia-border bg-dosia-bg-input px-2 py-1.5 text-sm text-dosia-text"
              aria-label="delegateCodexMaxSandbox"
            >
              {DELEGATE_CODEX_MAX_SANDBOX.map((m) => (
                <option key={m} value={m}>
                  {DELEGATE_CODEX_MAX_SANDBOX_LABEL[m] ?? m}
                </option>
              ))}
            </select>
          </FormField>
          <FormField
            label="默认委派模型 (defaultModel)"
            hint="仅企业通道生效；缺省用可用清单首个"
          >
            <TextInput
              value={value.delegateCodexDefaultModel}
              onChange={(e) => patch({ delegateCodexDefaultModel: e.target.value })}
              placeholder="留空 = 用可用清单首个"
              aria-label="delegateCodexDefaultModel"
            />
          </FormField>
          <label className="flex items-center gap-2 cursor-pointer col-span-2">
            <input
              type="checkbox"
              checked={value.delegateCodexReviewGate}
              onChange={(e) => patch({ delegateCodexReviewGate: e.target.checked })}
              aria-label="delegateCodexReviewGate"
            />
            <span className="text-sm text-dosia-text">
              停笔前强制评审（reviewGate · 委派改动过文件后本轮主脑先复核才能停笔）
            </span>
          </label>
        </div>
      ) : null}
    </div>
  );
}

/**
 * managedSettings JSON 框里 `brainGroupDefaults`（组名 → 组默认）中已从网关下线的模型（N-88）。
 * 判据与下发同一个纯函数（shared `pruneBrainGroupDefaultsToCatalog`，admin-portal
 * `resolveRoleBrainGroupDefaultsForDelivery` 剔的就是这些）：个人钱包组不算，`inherit` / Claude 裸别名不算。
 * `liveCatalog` 未给（这次没有网关现行清单）、JSON 解析不了、或不是对象 → 空，一个标记都不出。
 */
export function retiredBrainGroupDefaults(
  managedSettingsJson: string,
  liveCatalog: readonly CatalogModelIdRow[] | undefined,
): BrainGroupDefaultsRemoval[] {
  if (!liveCatalog || liveCatalog.length === 0 || !managedSettingsJson.trim()) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(managedSettingsJson);
  } catch {
    return [];
  }
  if (!isPlainObject(parsed)) return [];
  const defaults = parsed.brainGroupDefaults;
  if (!isPlainObject(defaults)) return [];
  // 逐组形状由 shared pruneRetiredGroupParams 再守一道（非对象的组原样跳过），与下发侧 brainGroupDefaultsOf 同形。
  return pruneBrainGroupDefaultsToCatalog(defaults as Record<string, BrainGroupParams>, liveCatalog).removed;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function PolicyEditor({
  value,
  onChange,
  highRiskReadOnly = false,
  liveCatalog,
}: {
  value: PolicyEditorValue;
  onChange: (next: PolicyEditorValue) => void;
  /** org-admin 视角：高危字段（templateId/disallowedTools/managedSettings/sandbox/hooks）
   *  只读展示（UX 层；硬门在服务端 forbidden-field 403）。 */
  highRiskReadOnly?: boolean;
  /** 网关现行清单（N-88「已下线」标记用）；未给 = 这次没有 live 清单，不出任何下线标记。 */
  liveCatalog?: readonly CatalogModelIdRow[];
}): JSX.Element {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showGovernance, setShowGovernance] = useState(false);
  const patch = (p: Partial<PolicyEditorValue>): void =>
    onChange({ ...value, ...p });
  const highRiskHint = highRiskReadOnly
    ? "平台管理字段 · 如需调整请联系 DOSIA 运营"
    : undefined;
  const retiredGroupDefaults = retiredBrainGroupDefaults(value.managedSettingsJson, liveCatalog);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-3">
        <FormField
          label="templateId"
          hint={highRiskHint ?? "workspace 模板"}
        >
          <TextInput
            value={value.templateId}
            onChange={(e) => patch({ templateId: e.target.value })}
            placeholder="default"
            disabled={highRiskReadOnly}
          />
        </FormField>
        <FormField label="effort">
          <TextInput
            value={value.effort}
            onChange={(e) => patch({ effort: e.target.value })}
            placeholder="medium"
          />
        </FormField>
      </div>

      <p className="text-xs text-dosia-text-dim">
        以上为基础项 · 全部留空即可（该角色员工获得默认工作面，全部技能/工具开放、登录即用）。
        下面是可选的权限收紧项，仅在需要精细管控本角色时展开。
      </p>

      <button
        type="button"
        onClick={() => setShowGovernance((x) => !x)}
        className="text-xs text-dosia-accent hover:underline self-start"
      >
        {showGovernance
          ? "收起权限与治理"
          : "展开权限与治理（可选 · 工具 / 插件 / 技能 / MCP / 预算 / 员工额度 / Agent 可见性）"}
      </button>

      {showGovernance ? (
      <div className="flex flex-col gap-3 border-t border-dosia-border pt-3">
      <FormField
        label="disallowedTools"
        hint={highRiskHint ?? "禁止使用的工具名，逗号分隔（Bash, Edit, ...）"}
      >
        <TextInput
          value={value.disallowedTools}
          onChange={(e) => patch({ disallowedTools: e.target.value })}
          placeholder="Bash, Edit"
          aria-label="disallowedTools"
          disabled={highRiskReadOnly}
        />
      </FormField>

      <div className="grid grid-cols-3 gap-3">
        <FormField label="pluginTags" hint="逗号分隔 tag">
          <TextInput
            value={value.pluginTags}
            onChange={(e) => patch({ pluginTags: e.target.value })}
          />
        </FormField>
        <FormField label="managedPlugins" hint="逗号分隔 plugin 名（白名单 · 强制启用）">
          <TextInput
            value={value.managedPlugins}
            onChange={(e) => patch({ managedPlugins: e.target.value })}
          />
        </FormField>
        <FormField label="forbiddenPlugins" hint="逗号分隔 plugin 名（黑名单 · 永不加载）">
          <TextInput
            value={value.forbiddenPlugins}
            onChange={(e) => patch({ forbiddenPlugins: e.target.value })}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FormField
          label="managedSkills"
          hint='"all" / 逗号分隔 skill 名（白名单）'
        >
          <TextInput
            value={value.managedSkills}
            onChange={(e) => patch({ managedSkills: e.target.value })}
            placeholder="all"
          />
        </FormField>
        <FormField label="forbiddenSkills" hint="逗号分隔 skill 名（黑名单）">
          <TextInput
            value={value.forbiddenSkills}
            onChange={(e) => patch({ forbiddenSkills: e.target.value })}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <FormField
          label="forbiddenMcpServers"
          hint="逗号分隔 MCP server 名（黑名单 · 禁用员工安装/工作区 MCP，不影响插件内置 MCP 与内置工具）"
        >
          <TextInput
            value={value.forbiddenMcpServers}
            onChange={(e) => patch({ forbiddenMcpServers: e.target.value })}
          />
        </FormField>
      </div>

      <McpServersField
        value={value.mcpServersJson}
        onChange={(mcpServersJson) => patch({ mcpServersJson })}
      />

      {/* 预算与模型策略（managedSettings 下放子字段）— org-admin 可编辑 */}
      <div className="rounded-md border border-dosia-border p-3 flex flex-col gap-3">
        <p className="text-xs font-medium text-dosia-text">
          预算与模型策略
          <span className="ml-2 font-normal text-dosia-text-dim">
            组织自治项 · 管本组织的钱与模型可见性
          </span>
        </p>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="单会话预算上限 (USD)" hint="员工 budget 输入超此值自动钳到上限；留空不限">
            <TextInput
              value={value.maxAllowedBudgetUsd}
              onChange={(e) => patch({ maxAllowedBudgetUsd: e.target.value })}
              placeholder="10"
              aria-label="maxAllowedBudgetUsd"
            />
          </FormField>
          <FormField label="effort 上限" hint="员工可选 effort 不超过此档；留空不限">
            <select
              value={value.maxAllowedEffort}
              onChange={(e) => patch({ maxAllowedEffort: e.target.value })}
              className="w-full rounded-md border border-dosia-border bg-dosia-bg-input px-2 py-1.5 text-sm text-dosia-text"
              aria-label="maxAllowedEffort"
            >
              <option value="">不限制</option>
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
              <option value="xhigh">xhigh</option>
              <option value="max">max</option>
            </select>
          </FormField>
          <FormField label="可见模型组" hint="逗号分隔组名；留空=全部 entitled 组">
            <TextInput
              value={value.visibleGroups}
              onChange={(e) => patch({ visibleGroups: e.target.value })}
              placeholder="claude-default"
              aria-label="visibleGroups"
            />
          </FormField>
        </div>
        {/* 模型可见性圈定 + 每模型参数已迁至统一「模型治理」tab（scope=角色），此处不再重复。 */}
        {/* G-K5 员工累计额度（顶层 dosia.memberQuota）— 与上方「单会话预算」正交：
            这里管 key 每天/每周最多花多少（网关 RateLimit1d/7d 强制），跑满即拦。 */}
        <div className="grid grid-cols-3 gap-3">
          <FormField label="员工日额度 (USD)" hint="该角色每员工每天最多花多少；跑满当日被网关拦；留空 = 无限">
            <TextInput
              value={value.memberQuotaDaily}
              onChange={(e) => patch({ memberQuotaDaily: e.target.value })}
              placeholder="不限"
              aria-label="memberQuotaDaily"
            />
          </FormField>
          <FormField label="员工周额度 (USD)" hint="该角色每员工每 7 天最多花多少；留空 = 无限">
            <TextInput
              value={value.memberQuotaWeekly}
              onChange={(e) => patch({ memberQuotaWeekly: e.target.value })}
              placeholder="不限"
              aria-label="memberQuotaWeekly"
            />
          </FormField>
          <FormField
            label="每员工每日云任务告警额度 (USD)"
            hint="按每位员工、每个北京日累计云端任务的模型花费：只调一次模型的任务按实际花费补差，完整对话类任务按单次上限预留估算（实际账单以网关为准）；正数与组织级额度取更小值；留空=继承组织级，只有组织级也未设置时才无限"
          >
            <TextInput
              value={value.cloudTaskDailyBudgetUsd}
              onChange={(e) => patch({ cloudTaskDailyBudgetUsd: e.target.value })}
              placeholder="不限"
              aria-label="cloudTaskDailyBudgetUsd"
            />
          </FormField>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={value.lockRoleMapping}
            onChange={(e) => patch({ lockRoleMapping: e.target.checked })}
            aria-label="lockRoleMapping"
          />
          <span className="text-sm text-dosia-text">
            锁定模型映射（员工只能看不能改 opus/sonnet/haiku → 具体模型的映射）
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={value.orgMemoryEnabled}
            onChange={(e) => patch({ orgMemoryEnabled: e.target.checked })}
            aria-label="orgMemoryEnabled"
          />
          <span className="text-sm text-dosia-text">
            启用记忆（默认开 · 取消则本角色员工的 DOSIA 记忆被强制关闭，员工无法自行打开）
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={value.memoryReadToggleAllowed}
            onChange={(e) => patch({ memoryReadToggleAllowed: e.target.checked })}
            aria-label="memoryReadToggleAllowed"
          />
          <span className="text-sm text-dosia-text">
            允许员工自关记忆读取（默认开 · 取消后员工端连读取开关都隐藏，写入仍恒开）
          </span>
        </label>
      </div>

      <PermissionsField value={value} patch={patch} />

      <ConnectorTiersField value={value} patch={patch} />

      <DelegateCodexField value={value} patch={patch} />

      <AgentVisibilityField
        templateId={value.templateId}
        bindings={value.agentRoleBindings}
        onChange={(agentRoleBindings) => patch({ agentRoleBindings })}
      />

      <button
        type="button"
        onClick={() => setShowAdvanced((x) => !x)}
        className="text-xs text-dosia-accent hover:underline self-start"
      >
        {showAdvanced ? "收起高级字段" : "展开高级字段（skillPolicy / managedSettings / sandbox / hooks）"}
      </button>

      {showAdvanced ? (
        <div className="flex flex-col gap-3 border-t border-dosia-border pt-3">
          <FormField
            label="skillPolicy (JSON)"
            hint="4-态 Skill 策略 — 复杂嵌套，本期 JSON 编辑；form UI 后续 PR 补"
          >
            <TextArea
              rows={6}
              value={value.skillPolicyJson}
              onChange={(e) => patch({ skillPolicyJson: e.target.value })}
              placeholder="{}"
            />
          </FormField>
          <FormField
            label="managedSettings 安全字段 (JSON)"
            hint={highRiskHint ?? "permissions / env 等安全子字段；预算与模型策略在上方结构化区编辑"}
          >
            <TextArea
              rows={4}
              value={value.managedSettingsJson}
              onChange={(e) => patch({ managedSettingsJson: e.target.value })}
              placeholder="{}"
              disabled={highRiskReadOnly}
            />
          </FormField>
          {retiredGroupDefaults.length > 0 ? (
            <div
              data-testid="group-defaults-retired-banner"
              className="flex flex-col gap-1 rounded border border-dosia-warning/40 bg-dosia-warning/10 px-3 py-2 text-xs text-dosia-warning"
            >
              {retiredGroupDefaults.map((r) => (
                <span key={`${r.group}:${r.modelId}`}>
                  {r.group} 组的默认模型 {r.modelId} 已下线：员工这一组会改为跟随主脑，你的设置不会被改动，建议换成在线模型。
                </span>
              ))}
            </div>
          ) : null}
          <FormField label="sandbox (JSON)" hint={highRiskHint}>
            <TextArea
              rows={3}
              value={value.sandboxJson}
              onChange={(e) => patch({ sandboxJson: e.target.value })}
              placeholder="{}"
              disabled={highRiskReadOnly}
            />
          </FormField>
          <FormField label="hooks (JSON, DosiaHookRule[])" hint={highRiskHint}>
            <TextArea
              rows={4}
              value={value.hooksJson}
              onChange={(e) => patch({ hooksJson: e.target.value })}
              placeholder="[]"
              disabled={highRiskReadOnly}
            />
          </FormField>
        </div>
      ) : null}
      </div>
      ) : null}
    </div>
  );
}
